/**
 * Hypotheses: human-authored claims of a case with append-only assessments (PRD-0075 E,
 * ADR-0095). Named apart from `types/hypothesis.ts`, which is the legacy Deep Research row.
 *
 * PARTNER FILES — `src/apps/api/spectre/api/routers/v1/schemas/case_analytic_hypotheses.py` and
 * `…/schemas/case_hypothesis_evidence.py`. These types must stay structurally identical to those
 * Pydantic DTOs; a change there lands in the same review as the matching change here. Times are
 * ISO 8601 strings.
 */

export const HYPOTHESIS_VERDICTS = ['supported', 'refuted', 'inconclusive'] as const
export type HypothesisVerdict = (typeof HYPOTHESIS_VERDICTS)[number]

export const HYPOTHESIS_CONFIDENCES = ['low', 'medium', 'high'] as const
export type HypothesisConfidence = (typeof HYPOTHESIS_CONFIDENCES)[number]

export const EVIDENCE_KINDS = ['investigation', 'document', 'alert', 'research_run'] as const
export type EvidenceKind = (typeof EVIDENCE_KINDS)[number]

export type AnalyticHypothesisStatus = 'open' | 'closed'

/** The reader's role in the case (a superuser reads as owner): a viewer only reads. */
export type HypothesisMemberRole = 'owner' | 'collaborator' | 'viewer'

/** `EvidenceRefIn`: an item an assessment cites. */
export interface EvidenceRefIn {
  kind: EvidenceKind
  id: string
}

/** `EvidenceRefOut`: a cited item with its name when it was cited (none for an alert). */
export interface EvidenceRefOut extends EvidenceRefIn {
  label: string | null
}

/** `AnalyticHypothesisCreate`. */
export interface AnalyticHypothesisCreate {
  title: string
  claim: string
  /** Stable entity ids (`_uid`), never element ids. */
  focus_uids: string[]
}

/** `HypothesisAssessmentCreate`: the author and the date are the server's. */
export interface HypothesisAssessmentCreate {
  verdict: HypothesisVerdict
  confidence: HypothesisConfidence
  rationale: string
  evidence: EvidenceRefIn[]
}

/** `AssessmentPointOut`: one step of the strip. */
export interface AssessmentPoint {
  id: string
  verdict: HypothesisVerdict
  confidence: HypothesisConfidence
  created_at: string
}

/** `HypothesisAssessmentOut`: never edited; a correction is a new one. */
export interface HypothesisAssessment {
  id: string
  hypothesis_id: string
  verdict: HypothesisVerdict
  confidence: HypothesisConfidence
  rationale: string
  evidence: EvidenceRefOut[]
  author_id: string | null
  author_name: string
  created_at: string
}

/** `FocusEntityOut`: a focus entity read from the graph by its uid. */
export interface FocusEntity {
  uid: string
  name: string | null
  label: string | null
  /** The node's element id now (a link into the graph); null when no node carries the uid. */
  node_id: string | null
}

/** `AnalyticHypothesisOut`. */
export interface AnalyticHypothesis {
  id: string
  case_id: string
  title: string
  claim: string
  focus_uids: string[]
  status: AnalyticHypothesisStatus
  created_by: string | null
  created_by_name: string
  created_at: string
  closed_at: string | null
  /** Every assessment as a step, oldest first. */
  strip: AssessmentPoint[]
  /** The last step: the hypothesis's current state. */
  latest: AssessmentPoint | null
}

/** `AnalyticHypothesisDetailOut`. */
export interface AnalyticHypothesisDetail extends AnalyticHypothesis {
  /** In the order of `focus_uids`. */
  focus_entities: FocusEntity[]
  /** Oldest first. */
  assessments: HypothesisAssessment[]
  member_role: HypothesisMemberRole
}

/** `AnalyticHypothesesResponse`: open hypotheses first, then the newest. */
export interface AnalyticHypothesesResponse {
  hypotheses: AnalyticHypothesis[]
  member_role: HypothesisMemberRole
}

/** `HypothesisAssessmentsResponse`: oldest first. */
export interface HypothesisAssessmentsResponse {
  assessments: HypothesisAssessment[]
}

/** `HypothesisRefusal`: the `detail` of a 409/422 the service gave. */
export interface HypothesisRefusal {
  code: 'hypothesis_closed' | 'unknown_focus_entities' | 'unknown_evidence'
  message: string
  items: string[]
}

// ─── What a hypothesis rests on (PRD-0075 R-E3/R-E4, `case_hypothesis_evidence.py`) ───────────

export const RESEARCH_RUN_STATUSES = [
  'queued',
  'running',
  'finished_success',
  'finished_partial',
  'finished_error',
] as const
export type ResearchRunStatus = (typeof RESEARCH_RUN_STATUSES)[number]

/** `HypothesisResearchRunOut`: a Deep Research run started from the hypothesis; it has no verdict. */
export interface HypothesisResearchRun {
  id: string
  /** The research the run belongs to (its page is `/cases/:caseId/search/:researchId`). */
  research_id: string
  title: string
  status: ResearchRunStatus
  started_at: string
  finished_at: string | null
}

/** `HypothesisResearchRunsResponse`: newest first. */
export interface HypothesisResearchRunsResponse {
  runs: HypothesisResearchRun[]
}

/** `AssessmentNoteOut`: an investigation note of kind assessment. */
export interface AssessmentNote {
  id: string
  text: string
  author_name: string
  created_at: string
}

/** `LinkedInvestigationOut`: an investigation that is evidence for the hypothesis. */
export interface LinkedInvestigation {
  id: string
  title: string
  /** The research run whose finding it was saved from; null when added by hand. */
  source_run_id: string | null
  included_in_report: boolean
  created_at: string
  /** Newest first. */
  assessment_notes: AssessmentNote[]
}

/** `LinkedInvestigationsResponse`: newest first. */
export interface LinkedInvestigationsResponse {
  investigations: LinkedInvestigation[]
}
